import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Popconfirm, Typography } from 'antd';
import { retryMemberCommunicationWindow } from '@/services/technical';

import { newRetryRequestId } from './FactSyncRetryButton';

export default function CommunicationWindowRetryButton({ task, memberId }) {
  const [state, setState] = useState({});
  const busy = useRef(false);
  const request = useRef(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  // 仅为按钮提示；真实资格及最新状态由服务端锁定任务后校验。
  const eligible =
    ['workwx_chat', 'guided_course', 'live_coaching'].includes(
      task.sourceType,
    ) &&
    task.status === 'failed' &&
    Number.isInteger(task.attemptCount) &&
    task.attemptCount >= 3 &&
    task.attemptCount <= 2147483647 &&
    Number.isSafeInteger(task.updatedAt) &&
    task.updatedAt > 0 &&
    task.updatedAt <= 8640000000000000 &&
    typeof task.taskRecordId === 'string' &&
    /^[1-9][0-9]{0,18}$/.test(task.taskRecordId) &&
    (task.taskRecordId.length < 19 ||
      task.taskRecordId <= '9223372036854775807');
  const retry = async () => {
    if (busy.current || state.accepted || state.conflict || !eligible) return;
    busy.current = true;
    setState({ loading: true });
    try {
      if (!request.current)
        request.current = {
          memberId,
          taskRecordId: task.taskRecordId,
          expectedAttemptCount: task.attemptCount,
          expectedUpdatedAt: task.updatedAt,
          requestId: newRetryRequestId(),
        };
      const response = await retryMemberCommunicationWindow({
        requestData: request.current,
      });
      const d = response?.data;
      if (
        !['00000', '0'].includes(String(response?.code)) ||
        String(d?.memberId) !== String(memberId) ||
        d?.taskRecordId !== task.taskRecordId ||
        d?.requestId !== request.current.requestId ||
        !['accepted', 'replayed'].includes(d?.outcome)
      )
        throw new Error('Unconfirmed retry');
      if (mounted.current) setState({ accepted: true });
    } catch (error) {
      const status = error?.response?.status;
      if (mounted.current)
        setState({
          conflict: status === 409,
          error:
            {
              401: '请先登录后台',
              403: '无会员档案菜单权限',
              409: '状态已变化或不符合重试条件，请刷新沟通状态',
            }[status] || '结果未确认，再次点击将沿用同一请求；也可刷新沟通状态',
        });
    } finally {
      busy.current = false;
    }
  };
  if (!eligible) return null;
  if (state.accepted)
    return (
      <Typography.Text>
        重试请求已受理，请刷新沟通状态；不代表清洗或标签提取完成
      </Typography.Text>
    );
  return (
    <>
      <Popconfirm
        title="重新入队这条失败任务？后续可能更新档案并触发模型调用与费用，不保证重试成功。"
        onConfirm={retry}
        okText="确认重新入队"
        cancelText="取消"
        disabled={state.loading || state.conflict}
      >
        <Button
          size="small"
          loading={state.loading}
          disabled={state.loading || state.conflict}
        >
          重试沟通清洗
        </Button>
      </Popconfirm>
      {state.error && (
        <Typography.Paragraph type="warning">
          {state.error}
        </Typography.Paragraph>
      )}
    </>
  );
}
CommunicationWindowRetryButton.propTypes = {
  task: PropTypes.object.isRequired,
  memberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
};
